import React, { useState } from 'react';
import { Link } from 'react-router-dom';
import {
  Sparkles, Code2, Globe, Search, Smartphone, Rocket, ChevronDown, ArrowRight, Calendar,
  CheckCircle, Star, Clock, Users, Award, LayoutDashboard, Notebook, MessageCircle, BookOpen,
  GraduationCap, Loader2, Send,
} from 'lucide-react';
import SEO from '../../components/SEO';
import Section from '../../components/v2/Section';
import { PrimaryCTA, SecondaryCTA } from '../../components/v2/CTAButton';
import TrainingCountdown from '../../components/TrainingCountdown';
import axios from 'axios';
import { toast } from 'sonner';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const MODULES = [
  {
    n: '01', icon: Sparkles, title: 'Foundations before you touch a pixel',
    tagline: 'The week most people skip, and pay for later.',
    bullets: [
      'Defining your ONE outcome: enquiries, sales, bookings, subscribers',
      'Niche, audience, and the 5-line brief you will feed every AI tool',
      'Domain selection: SEO impact, what to never buy',
      'Brand assets: logo, colours, fonts, done in 30 mins with AI not Canva',
      'Legal must-haves: GDPR cookie, T&Cs, privacy policy templates',
    ],
  },
  {
    n: '02', icon: Code2, title: 'Building the site with AI',
    tagline: 'Blank screen to working homepage in 60 minutes.',
    bullets: [
      'Tour of the AI builder: prompts that produce, prompts that don’t',
      'Writing your home page in plain English',
      'Hero, value proposition, social proof, primary CTA, what each does',
      'Mobile-first thinking: how to test as you build',
      'Translating “make it pop” into something AI can actually use',
    ],
  },
  {
    n: '03', icon: Globe, title: 'Pages, content & conversion',
    tagline: 'Multi-page site, lead capture, and the layouts that actually convert.',
    bullets: [
      'Services / Packages / Pricing / About / Contact / Blog, what to include',
      'Booking forms, contact forms, mailing list capture',
      'Image rules: AI images vs stock vs your own photos',
      'Copy frameworks (PAS, AIDA), getting AI copy that doesn’t sound like AI',
      'Every page gets a CTA: what to say, where to say it',
    ],
  },
  {
    n: '04', icon: Search, title: 'SEO & AI search visibility',
    tagline: 'Found on Google in 6 weeks, including ChatGPT and Perplexity.',
    bullets: [
      'The 31-point SEO audit explained in plain English',
      'Meta titles, descriptions, schema markup, what they do',
      'Google Search Console & Bing Webmaster setup (live)',
      'Google Business Profile setup for local businesses',
      'AI visibility: getting recommended by ChatGPT, Perplexity, Claude',
    ],
  },
  {
    n: '05', icon: Smartphone, title: 'A mobile app in a day',
    tagline: 'A real app, without learning to code.',
    bullets: [
      'Mobile app vs mobile-friendly website: which do you actually need',
      'Building a Progressive Web App from your existing site',
      'Native app option: what is possible, what is the cost trade-off',
      'Integrations that punch above their weight: bookings, payments, email',
      'Reviews (Trustpilot, Google) wired into your site',
    ],
  },
  {
    n: '06', icon: Rocket, title: 'Launch day & ongoing maintenance',
    tagline: 'Going live without faceplanting.',
    bullets: [
      'Pre-launch checklist, the one Jo uses for client launches',
      'Going live: connecting your domain, SSL, redirects, 404s',
      'Launch day social media playbook with templates',
      'Analytics: GA4 setup and the 3 numbers you actually need weekly',
      'Maintenance routine: 30 mins per week, what to check',
    ],
  },
];

const TIERS = [
  {
    name: 'Self-Paced',
    price: '£295',
    desc: 'All 6 module recordings + workbook + lifetime access to the Members Portal. Build at your own pace.',
    features: [
      'Lifetime access to the Members Portal',
      'All 6 module videos in your dashboard',
      'PDF workbooks (one per module)',
      'Personal journal to track your build',
      'Cohort community feed',
      'Certificate of Completion at 100%',
      'Live group Q&A every 6 weeks',
    ],
    payLink: 'https://wise.com/pay/r/TVEYuAzva49h9mw',
    popular: false,
    testId: 'training-tier-self',
  },
  {
    name: 'Live Cohort',
    price: '£795',
    desc: 'Six weeks. One small group. Real-time builds with Jo. The portal goes everywhere with you.',
    features: [
      'Everything in Self-Paced, plus:',
      '6 × 1-hr LIVE sessions with Jo + optional 1-hr Q&A each week',
      'Build your site alongside the group, in real time',
      'One 30-min 1-to-1 site review call',
      'Active cohort feed, peers cheering you on',
      'Pinned cohort calls, replays, announcements inside the portal',
      'Lifetime access to all future programme updates',
    ],
    payLink: 'https://wise.com/pay/r/YGeZYq9yewPBTGQ',
    popular: true,
    testId: 'training-tier-live',
  },
  {
    name: 'VIP Done-With-You',
    price: '£1,995',
    desc: 'Live cohort + fortnightly 1-to-1s with Jo + WhatsApp access. The fastest route to launch.',
    features: [
      'Everything in Live Cohort, plus:',
      '3 × 60-min 1-to-1 calls with Jo',
      'Direct WhatsApp access throughout the 6 weeks',
      'Jo personally reviews your launch site',
      '1 month of Done-For-You SEO after launch (worth £295)',
      'Priority replies in the Ask Jo channel',
      'Strictly limited VIP places per cohort',
    ],
    payLink: 'https://wise.com/pay/r/9oOJ_P_07Uj6GmA',
    popular: false,
    testId: 'training-tier-vip',
  },
];

const PORTAL_FEATURES = [
  { icon: BookOpen,      t: '6 module dashboards', d: 'Videos, workbooks, task checklists.' },
  { icon: Notebook,      t: 'Personal journal',    d: 'Capture wins and blockers as you build.' },
  { icon: MessageCircle, t: '5-channel feed',      d: 'Intros, Announcements, Ask Jo, Show & Tell, Wins.' },
  { icon: Users,         t: 'Cohort directory',    d: 'Profiles, bios, social links, your network.' },
  { icon: Award,         t: 'Auto certificate',    d: 'Issued the moment you hit 100% completion.' },
];

const FAQS = [
  { q: 'Do I need any tech experience?', a: 'Absolutely not. The whole point is showing non-technical business owners how to build a professional website and app using AI. If you can describe your business in plain English, you can do this.' },
  { q: 'What exactly is the Members Portal, and how do I access it?', a: 'Every delegate gets a personal login to the Audio & Co Training Portal, a custom-built Progressive Web App built using the same AI methods you’re about to learn. Inside: modules dashboard with all 6 videos and workbooks, personal journal, cohort community feed with 5 channels (Introductions, Announcements, Ask Jo, Show & Tell, Wins & Milestones), and your Certificate of Completion. Login details are emailed 48 hours before the first session. Jo walks you through the portal in Session 1.' },
  { q: 'What is included in the training fee?', a: 'The fee covers the training itself: live sessions or recordings inside the Members Portal, the workbooks, cohort community access, the certificate, and Jo teaching you the exact methods used on Audio & Co. and PopCrown. It does NOT include third-party costs you need to run your own site: AI builder subscription (~£20/month), domain (~£10/year), AI credits (~£20–£50 typical), and any optional integrations.' },
  { q: 'What if I miss a live session?', a: 'Every live session is recorded and uploaded to your Members Portal within 24 hours. Watch back at your own pace and post questions in the Ask Jo channel.' },
  { q: 'Can I pay in instalments?', a: 'Yes, payment plans are available on request. Email hello@audioandco.com BEFORE signing up and Jo will send you a custom invoice broken into 2 or 3 payments (depending on the tier). Training does NOT begin until the 14-day statutory cooling-off period has expired. Once training begins, instalments continue alongside the cohort, there is no requirement to clear the full balance up front. Late instalments may incur a £25 admin fee and portal access may be paused until the account is brought up to date.' },
  { q: 'Will I have a working website at the end?', a: 'If you follow the programme and do the work each week, yes, by the end of Module 6 you will have a live site on your own domain. The onus is on you to apply what you learn between sessions. Most committed delegates also leave with a basic mobile app (PWA), like the Members Portal itself.' },
  { q: 'Can I bring my existing business or do I need a new idea?', a: 'Either works. If you already have a business but no website (or a bad one), you will rebuild it during the cohort. Starting fresh? Even better, we will go through Module 1 in detail to nail your foundations.' },
  { q: 'What is your refund policy?', a: 'You get a 14-day cooling-off period from the moment the cart closes (UK Consumer Contracts Regulations 2013). Training only begins AFTER the cooling-off period has expired. Full refund in those 14 days, no questions asked. Once training starts there are no refunds. Payment plans continue per schedule. See the full Training Terms & Conditions.' },
  { q: 'When does the next cohort start?', a: 'The next live cohort starts Tuesday 28th July at 7:30pm UK time. 1 hour of live teaching + optional 1-hour Q&A each Tuesday for 6 weeks. Cart closes Wednesday 9th July 23:59 UK. Members Portal login arrives 48 hours before kickoff.' },
];

// Cart-close cut-off: Wednesday 9 July 2026, 23:59 UK (22:59 UTC in BST)
const CART_CLOSE_ISO = '2026-07-09T22:59:00Z';
const BOOK_CALL_URL = 'https://audioandco.17hats.com/p#/scheduling/rhrvxsrbdknkddgbdpbhncsvwndbhtdf';

export default function TrainingV2() {
  const [openFaq, setOpenFaq] = useState(null);
  const [acceptedTerms, setAcceptedTerms] = useState(false);
  const [interest, setInterest] = useState({ name: '', email: '', business: '' });
  const [sending, setSending] = useState(false);
  const [sent, setSent] = useState(false);
  const [cartClosed, setCartClosed] = useState(() => Date.now() >= new Date(CART_CLOSE_ISO).getTime());

  // Re-check every minute so buttons flip live at midnight without a refresh
  React.useEffect(() => {
    if (cartClosed) return;
    const id = setInterval(() => {
      if (Date.now() >= new Date(CART_CLOSE_ISO).getTime()) setCartClosed(true);
    }, 60_000);
    return () => clearInterval(id);
  }, [cartClosed]);

  const scrollToInterest = (e) => {
    e.preventDefault();
    document.querySelector('[data-testid="training-interest"]')?.scrollIntoView({ behavior: 'smooth' });
  };

  const submitInterest = async (e) => {
    e.preventDefault();
    setSending(true);
    try {
      await axios.post(`${API}/contact`, {
        name: interest.name,
        email: interest.email,
        message: `Training programme interest. Business: ${interest.business || '(not provided)'}`,
        service_category: 'Training Programme Interest',
      });
      setSent(true);
      toast.success('Got it, Jo will be in touch.');
    } catch (err) {
      toast.error('Something went wrong, please email hello@audioandco.com directly.');
    } finally {
      setSending(false);
    }
  };

  return (
    <main className="font-body text-[#475569] bg-[#F6F8FB]">
      <SEO
        title="Podcast Training for Coaches | Audio & Co."
        description="Learn to build your own website and mobile app with AI. Live 6-week training programme for small business owners. Includes lifetime Members Portal access. From £295."
        keywords={['ai website builder course', 'website training course uk', 'build website without coding', 'no code website training uk', 'small business website training', 'ai web design course', 'progressive web app course', 'jo day training']}
        url="https://audioandco.com/training"
      />

      {/* HERO */}
      <Section bg="bg" innerClassName="!py-12 sm:!py-20" testId="training-page">
        <p className="text-[#BC516F] font-semibold text-sm uppercase tracking-widest">6-week live cohort programme</p>
        <h1 className="font-display font-bold text-[#1E2A44] text-4xl sm:text-5xl lg:text-6xl leading-[1.1] tracking-tight mt-3 max-w-4xl">
          Build your own website &amp; app in 6 weeks. No code. No agency.
        </h1>
        <p className="text-lg sm:text-xl text-[#475569] mt-6 max-w-3xl leading-relaxed">
          Tired of £5,000 web quotes? Sick of paying £200/hour to swap a photo? Learn the exact AI methods
          Jo uses for Audio &amp; Co. and her own businesses, including the Members Portal you&rsquo;ll log into
          from day one. Real website. Real app. Skills you keep for life.
        </p>

        <div className="mt-8 max-w-2xl">
          <TrainingCountdown />
        </div>

        <div className="mt-2 flex flex-wrap items-center gap-6 text-sm text-[#64748B]">
          <span className="inline-flex items-center gap-1.5"><Calendar className="w-4 h-4 text-[#BC516F]" /> Next cohort: Tue 28 July, 7:30pm UK</span>
          <span className="inline-flex items-center gap-1.5"><Clock className="w-4 h-4 text-[#BC516F]" /> 1hr live + 1hr Q&amp;A weekly</span>
          <span className="inline-flex items-center gap-1.5"><Users className="w-4 h-4 text-[#BC516F]" /> Small cohort</span>
        </div>
      </Section>

      {/* IMAGINE / OUTCOMES */}
      <Section bg="white" testId="training-outcomes">
        <p className="text-[#BC516F] font-semibold text-sm uppercase tracking-widest">Imagine, 6 weeks from now…</p>
        <h2 className="font-display font-bold text-[#1E2A44] text-3xl sm:text-4xl tracking-tight mt-2 max-w-3xl">
          It&rsquo;s Tuesday evening. You click <span className="text-[#BC516F]">Publish</span>.
        </h2>
        <p className="text-[#475569] mt-4 max-w-3xl leading-relaxed text-lg">
          Your domain resolves. Your hero image is yours. The words sound like you, not a robot. Your booking form
          is live. Your phone pings, <em className="text-[#1E2A44] not-italic font-semibold">first enquiry</em>.
          You did this. <span className="text-[#1E2A44] font-semibold">You. Built. This.</span>
        </p>
        <div className="mt-10 grid sm:grid-cols-2 gap-4">
          {[
            { icon: Globe,     t: 'A live website on your own domain',  d: 'Indexed by Google. Fast on mobile. Ready to take enquiries the minute it goes live.' },
            { icon: Smartphone, t: 'A working mobile app (PWA)',          d: 'Installs straight to your customer’s phone home screen, no app store, no fees.' },
            { icon: Search,    t: 'SEO that actually ranks',              d: 'Audited and optimised for Google AND the new wave of AI search, ChatGPT, Perplexity, Claude.' },
            { icon: LayoutDashboard, t: 'Lifetime Members Portal access', d: 'Your modules, journal, cohort feed, and certificate all in one custom-built PWA. Forever.' },
            { icon: GraduationCap, t: 'A Certificate of Completion',      d: 'Auto-issued the moment you finish the programme. Printable. Proof you did the work.' },
            { icon: Code2,     t: 'Skills you keep for life',              d: 'Edit and extend it yourself, and do it again for any business you ever run.' },
          ].map((b, i) => {
            const Icon = b.icon;
            return (
              <div key={i} className="flex gap-4 bg-[#F6F8FB] border border-[#E2E8F0] rounded-[14px] p-5">
                <div className="w-11 h-11 shrink-0 rounded-lg bg-white border border-[#E2E8F0] flex items-center justify-center">
                  <Icon className="w-5 h-5 text-[#BC516F]" />
                </div>
                <div>
                  <h3 className="font-display font-semibold text-[#1E2A44] text-base">{b.t}</h3>
                  <p className="text-[#64748B] text-sm mt-1 leading-relaxed">{b.d}</p>
                </div>
              </div>
            );
          })}
        </div>
      </Section>

      {/* MEMBERS PORTAL */}
      <Section bg="bg" testId="training-portal">
        <div className="grid lg:grid-cols-2 gap-12 items-start">
          <div>
            <p className="text-[#BC516F] font-semibold text-sm uppercase tracking-widest">Your private members area</p>
            <h2 className="font-display font-bold text-[#1E2A44] text-3xl sm:text-4xl tracking-tight mt-2">
              A custom app, built by Jo. With the exact methods you&rsquo;re about to learn.
            </h2>
            <p className="text-[#475569] mt-5 leading-relaxed">
              Your login lands in your inbox <span className="text-[#1E2A44] font-semibold">48 hours before the first session</span> ,
              install it to your phone, set up your profile, meet the cohort. Jo gives you a guided tour in Session 1 so
              you know exactly where everything lives.
            </p>
            <p className="text-[#475569] mt-4 leading-relaxed">
              This isn&rsquo;t a generic Kajabi or Circle subscription. This is the proof.{' '}
              <em className="text-[#BC516F] not-italic font-semibold">Show, don&rsquo;t tell.</em>
            </p>
          </div>
          <div className="space-y-3">
            {PORTAL_FEATURES.map((f, i) => {
              const Icon = f.icon;
              return (
                <div key={i} className="flex items-start gap-4 bg-white border border-[#E2E8F0] rounded-[14px] p-4">
                  <div className="w-11 h-11 rounded-lg bg-[#F6F8FB] border border-[#E2E8F0] flex items-center justify-center shrink-0">
                    <Icon className="w-5 h-5 text-[#BC516F]" />
                  </div>
                  <div>
                    <h3 className="font-display font-semibold text-[#1E2A44]">{f.t}</h3>
                    <p className="text-[#64748B] text-sm mt-0.5 leading-relaxed">{f.d}</p>
                  </div>
                </div>
              );
            })}
          </div>
        </div>
      </Section>

      {/* MODULES */}
      <Section bg="white" testId="training-modules">
        <p className="text-[#BC516F] font-semibold text-sm uppercase tracking-widest">The curriculum</p>
        <h2 className="font-display font-bold text-[#1E2A44] text-3xl sm:text-4xl tracking-tight mt-2 max-w-3xl">6 modules. 6 weeks. One live website built together.</h2>
        <p className="text-[#64748B] mt-3 max-w-2xl">Each week: 1 hour focused teaching with Jo + optional 1-hour Q&amp;A clinic. Jo builds her demo live on screen. You build yours in parallel.</p>
        <div className="mt-10 space-y-4">
          {MODULES.map(m => {
            const Icon = m.icon;
            return (
              <article key={m.n} className="bg-[#F6F8FB] border border-[#E2E8F0] rounded-[14px] p-6 sm:p-7">
                <div className="flex items-start gap-5">
                  <div className="font-display text-3xl sm:text-4xl font-bold text-[#BC516F] leading-none w-14 shrink-0">{m.n}</div>
                  <div className="flex-1 min-w-0">
                    <div className="flex items-start gap-3">
                      <Icon className="w-5 h-5 text-[#1E2A44] mt-1 shrink-0" />
                      <div>
                        <h3 className="font-display font-semibold text-[#1E2A44] text-xl">{m.title}</h3>
                        <p className="text-[#BC516F] text-sm font-medium mt-0.5">{m.tagline}</p>
                      </div>
                    </div>
                    <ul className="mt-4 space-y-1.5 ml-8">
                      {m.bullets.map((b, i) => (
                        <li key={i} className="flex items-start gap-2 text-[15px] text-[#475569]">
                          <CheckCircle className="w-4 h-4 text-[#BC516F] shrink-0 mt-1" /> {b}
                        </li>
                      ))}
                    </ul>
                  </div>
                </div>
              </article>
            );
          })}
        </div>
      </Section>

      {/* TIERS */}
      <Section bg="bg" testId="training-tiers" id="tiers">
        <div className="text-center max-w-2xl mx-auto">
          <p className="text-[#BC516F] font-semibold text-sm uppercase tracking-widest">Pricing</p>
          <h2 className="font-display font-bold text-[#1E2A44] text-3xl sm:text-4xl tracking-tight mt-2">Pick the route that fits.</h2>
          <p className="text-[#64748B] mt-3">All tiers include lifetime access to the Members Portal.</p>
        </div>

        {/* T&Cs gate (only shown while cart is open) */}
        {!cartClosed && (
          <label className="mt-8 max-w-2xl mx-auto flex items-start gap-3 bg-white border border-[#E2E8F0] rounded-[14px] p-4 cursor-pointer" data-testid="training-tc-checkbox-wrap">
            <input
              type="checkbox"
              checked={acceptedTerms}
              onChange={e => setAcceptedTerms(e.target.checked)}
              className="mt-1 w-4 h-4 accent-[#BC516F]"
              data-testid="training-tc-checkbox"
            />
            <span className="text-sm text-[#475569] leading-relaxed">
              I&rsquo;ve read and agree to the{' '}
              <Link to="/training-terms" className="text-[#BC516F] hover:text-[#A7468A] font-semibold underline">Training Terms &amp; Conditions</Link>
              {' '}(including the 14-day cooling-off period, no-refund-once-training-starts, and payment-plan rules).
            </span>
          </label>
        )}

        {cartClosed && (
          <div className="mt-8 max-w-2xl mx-auto bg-white border-2 border-[#BC516F] rounded-[14px] p-6 sm:p-7 text-center" data-testid="cart-closed-banner">
            <p className="text-[#BC516F] font-display font-bold text-lg">Sorry, this cohort is now closed.</p>
            <p className="text-[#475569] mt-2 text-sm">Register your details below to be kept informed of the next cohort, or book a free 30-minute call with Jo about the Pro Plan.</p>
          </div>
        )}

        <div className="mt-8 grid md:grid-cols-3 gap-5">
          {TIERS.map(t => {
            const buyDisabled = cartClosed || !acceptedTerms;
            return (
            <div
              key={t.name}
              className={`relative bg-white border rounded-[14px] p-6 sm:p-7 flex flex-col ${t.popular && !cartClosed ? 'border-[#BC516F] shadow-[0_14px_40px_rgba(188,81,111,0.18)]' : 'border-[#E2E8F0] shadow-[0_10px_30px_rgba(30,42,68,0.04)]'} ${cartClosed ? 'opacity-90' : ''}`}
              data-testid={t.testId}
            >
              {t.popular && !cartClosed && (
                <div className="absolute -top-3 left-1/2 -translate-x-1/2 bg-[#BC516F] text-white text-[10px] uppercase tracking-widest font-bold px-3 py-1 rounded-full">
                  Most popular
                </div>
              )}
              <h3 className="font-display font-bold text-[#1E2A44] text-xl">{t.name}</h3>
              <div className="font-display text-4xl font-bold text-[#1E2A44] mt-3">{t.price}<span className="text-base text-[#64748B] font-medium"> one-off</span></div>
              <p className="text-sm text-[#64748B] mt-3 leading-relaxed">{t.desc}</p>
              <ul className="mt-5 space-y-2 flex-1">
                {t.features.map((f, i) => (
                  <li key={i} className="flex items-start gap-2 text-sm text-[#475569]">
                    <CheckCircle className="w-4 h-4 text-[#BC516F] shrink-0 mt-0.5" /> {f}
                  </li>
                ))}
              </ul>

              {cartClosed ? (
                <div className="mt-6 space-y-2" data-testid={`${t.testId}-closed`}>
                  <div className="w-full inline-flex items-center justify-center gap-2 font-semibold rounded-[10px] px-5 py-3 bg-[#F6F8FB] text-[#64748B] border border-[#E2E8F0] cursor-not-allowed">
                    Cohort closed
                  </div>
                  <button
                    onClick={scrollToInterest}
                    className="w-full inline-flex items-center justify-center gap-2 text-sm font-semibold rounded-[10px] px-5 py-2.5 border-2 border-[#1E2A44] text-[#1E2A44] hover:bg-[#1E2A44] hover:text-white transition"
                    data-testid={`${t.testId}-register-interest`}
                  >
                    Register for next cohort <ArrowRight className="w-4 h-4" />
                  </button>
                  <a
                    href={BOOK_CALL_URL}
                    target="_blank" rel="noopener noreferrer"
                    className="w-full inline-flex items-center justify-center gap-2 text-sm font-semibold rounded-[10px] px-5 py-2.5 bg-[#BC516F] hover:bg-[#9A3A54] text-white transition shadow-[0_10px_30px_rgba(188,81,111,0.25)]"
                    data-testid={`${t.testId}-book-call`}
                  >
                    Or book a free call <ArrowRight className="w-4 h-4" />
                  </a>
                </div>
              ) : (
                <a
                  href={buyDisabled ? '#' : t.payLink}
                  target="_blank" rel="noopener noreferrer"
                  onClick={e => { if (buyDisabled) { e.preventDefault(); toast.error('Please accept the Terms & Conditions first'); } }}
                  className={`mt-6 inline-flex items-center justify-center gap-2 font-semibold rounded-[10px] px-5 py-3 transition ${
                    acceptedTerms
                      ? 'bg-[#BC516F] hover:bg-[#9A3A54] text-white shadow-[0_10px_30px_rgba(188,81,111,0.25)]'
                      : 'bg-[#F6F8FB] text-[#64748B] cursor-not-allowed border border-[#E2E8F0]'
                  }`}
                  data-testid={`${t.testId}-buy`}
                >
                  Buy {t.name} <ArrowRight className="w-4 h-4" />
                </a>
              )}
            </div>
          );})}
        </div>
        <p className="text-center text-xs text-[#64748B] mt-6 max-w-2xl mx-auto">
          Prices in GBP. <span className="text-[#475569]">Payment plans available on request</span> (email{' '}
          <a href="mailto:hello@audioandco.com" className="text-[#BC516F] hover:text-[#A7468A]">hello@audioandco.com</a>) ,
          split into 2 or 3 payments, no requirement to clear up front. 14-day cooling-off from cart close. Training only begins after the cooling-off period, no refunds once training has started.
        </p>
      </Section>

      {/* FAQ */}
      <Section bg="white" testId="training-faq">
        <h2 className="font-display font-bold text-[#1E2A44] text-3xl sm:text-4xl tracking-tight">Frequently asked questions</h2>
        <div className="mt-8 max-w-3xl space-y-3">
          {FAQS.map((f, i) => (
            <div key={i} className="bg-[#F6F8FB] border border-[#E2E8F0] rounded-[14px] overflow-hidden">
              <button
                onClick={() => setOpenFaq(openFaq === i ? null : i)}
                className="w-full flex items-center justify-between gap-4 text-left px-5 py-4 hover:bg-white transition"
                data-testid={`training-faq-toggle-${i}`}
              >
                <span className="font-display font-semibold text-[#1E2A44] text-[16px] sm:text-[17px]">{f.q}</span>
                <ChevronDown className={`w-5 h-5 text-[#64748B] shrink-0 transition ${openFaq === i ? 'rotate-180' : ''}`} />
              </button>
              {openFaq === i && (
                <div className="px-5 pb-5 text-[#475569] leading-relaxed">{f.a}</div>
              )}
            </div>
          ))}
        </div>
      </Section>

      {/* INTEREST FORM (for those who miss this cohort) */}
      <Section bg="bg" testId="training-interest">
        <div className="max-w-2xl mx-auto bg-white border border-[#E2E8F0] rounded-[14px] p-6 sm:p-8 shadow-[0_10px_30px_rgba(30,42,68,0.06)]">
          <h2 className="font-display font-bold text-[#1E2A44] text-2xl sm:text-3xl tracking-tight">Miss this cohort? Register your interest for the next one.</h2>
          <p className="text-[#64748B] mt-2">No pressure, no spam, just an email when the next cohort opens.</p>

          {sent ? (
            <div className="mt-6 bg-[#D9FBEA]/40 border border-[#D9FBEA] rounded-[14px] p-5 text-center" data-testid="training-interest-sent">
              <CheckCircle className="w-8 h-8 text-[#1E2A44] mx-auto mb-2" />
              <p className="text-[#1E2A44] font-semibold">Got it. Jo will be in touch.</p>
            </div>
          ) : (
            <form onSubmit={submitInterest} className="mt-6 space-y-3" data-testid="training-interest-form">
              <input
                type="text" required placeholder="Your name"
                value={interest.name} onChange={e => setInterest({...interest, name: e.target.value})}
                className="w-full bg-white border border-[#E2E8F0] rounded-[10px] px-4 py-3 text-[#1E2A44] focus:border-[#BC516F] focus:ring-2 focus:ring-[#BC516F]/15 outline-none"
                data-testid="training-interest-name"
              />
              <input
                type="email" required placeholder="Your email"
                value={interest.email} onChange={e => setInterest({...interest, email: e.target.value})}
                className="w-full bg-white border border-[#E2E8F0] rounded-[10px] px-4 py-3 text-[#1E2A44] focus:border-[#BC516F] focus:ring-2 focus:ring-[#BC516F]/15 outline-none"
                data-testid="training-interest-email"
              />
              <input
                type="text" placeholder="Your business (optional)"
                value={interest.business} onChange={e => setInterest({...interest, business: e.target.value})}
                className="w-full bg-white border border-[#E2E8F0] rounded-[10px] px-4 py-3 text-[#1E2A44] focus:border-[#BC516F] focus:ring-2 focus:ring-[#BC516F]/15 outline-none"
                data-testid="training-interest-business"
              />
              <button
                type="submit"
                disabled={sending}
                className="w-full inline-flex items-center justify-center gap-2 bg-[#BC516F] hover:bg-[#9A3A54] text-white font-semibold rounded-[10px] px-5 py-3 disabled:opacity-60"
                data-testid="training-interest-submit"
              >
                {sending ? <Loader2 className="w-4 h-4 animate-spin" /> : <Send className="w-4 h-4" />}
                Register interest
              </button>
            </form>
          )}
        </div>
      </Section>

      {/* FINAL CTA */}
      <Section bg="navy" innerClassName="!py-16">
        <div className="text-center max-w-2xl mx-auto">
          <h2 className="font-display font-bold text-white text-3xl sm:text-4xl tracking-tight">6 weeks. A live website. Skills for life.</h2>
          <p className="text-white/75 mt-4 text-lg">Lock in your seat for the next cohort.</p>
          <div className="mt-7 flex flex-col sm:flex-row items-center justify-center gap-4">
            <a
              href="#tiers"
              onClick={(e) => { e.preventDefault(); document.getElementById('tiers').scrollIntoView({ behavior: 'smooth' }); }}
              className="inline-flex items-center gap-2 font-semibold text-white bg-[#BC516F] hover:bg-[#9A3A54] rounded-[10px] px-7 py-3.5 shadow-[0_10px_30px_rgba(188,81,111,0.3)] transition"
              data-testid="training-final-cta"
            >
              See pricing <ArrowRight className="w-5 h-5" />
            </a>
            <Link to="/training-terms" className="text-sm text-white/70 hover:text-white">Read the Terms &amp; Conditions →</Link>
          </div>
        </div>
      </Section>
    </main>
  );
}
